import axios from 'axios';
import { ChatGroupId } from '@/chat/models/ChatGroup';
import CleaningStatus from '@/dossier/constants/CleaningStatus';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import { BrandId } from '@/shared/models/Brand';
import { OutletId } from '@/shared/models/Outlet';
import { UserId } from '@/shared/models/User';
import { present } from '@/shared/utils/typescript';
import TaskStatus from '../constants/TaskStatus';
import { parseAssignedDossierTasks } from '../converters/assignedDossierTaskConverters';
import { parseCleaningTasks } from '../converters/cleaningTaskConverters';
import { parseInvoicingSummary } from '../converters/invoicingSummaryConverters';
import { parseTaskContainer } from '../converters/taskContainerConverters';
import AssignedDossierTask from '../models/AssignedDossierTask';
import CleaningTask from '../models/CleaningTask';
import InvoicingSummary from '../models/InvoicingSummary';
import PlanningTask from '../models/PlanningTask';
import Task, { TaskId } from '../models/Task';
import TaskContainer from '../models/TaskContainer';

export interface AssignedDossiersCriteria {
  outletIds: Array<OutletId>;
  brandId: BrandId | null;
  teamId: WorkshopTeamId | null;
}

export interface TaskCriteria extends AssignedDossiersCriteria {
  status: TaskStatus;
}

interface LoadOptions extends Partial<TaskCriteria> {
  isPlanningOnly?: boolean;
  dossierId?: DossierId;
}

export interface CleaningCriteria extends AssignedDossiersCriteria {
  interiorCleaningStatus: CleaningStatus | null,
  exteriorCleaningStatus: CleaningStatus | null,
}

export default class TaskClient {
  static loadAll(criteria: TaskCriteria): Promise<TaskContainer> {
    return TaskClient.doLoadAll(criteria);
  }

  static async loadPlanningTasks(criteria: TaskCriteria): Promise<Array<PlanningTask>> {
    const container = await TaskClient.doLoadAll({ ...criteria, isPlanningOnly: true });
    return container.planningTasks;
  }

  static async loadByDossier(dossierId: DossierId): Promise<Array<Task>> {
    const container = await TaskClient.doLoadAll({ dossierId });
    return container.tasks;
  }

  private static async doLoadAll(options: LoadOptions): Promise<TaskContainer> {
    const params = {
      moboOutletIds: options.outletIds,
      brandId: options.brandId,
      teamId: options.teamId,
      // NOTE that this field is ignored when dossierId is present, but it's always required
      isResolved: options.status === TaskStatus.RESOLVED,
      dossierId: options.dossierId,
      isPlanningOnly: options.isPlanningOnly ?? false,
    };

    const response = await axios.get('/api/mobopro/v1/taskslist', { params });
    return present(parseTaskContainer(response.data));
  }

  static async loadCleaningTasks(criteria: CleaningCriteria): Promise<Array<CleaningTask>> {
    const response = await axios.post('api/mobopro/v1/dossiers/cleaning', {
      moboOutletIds: criteria.outletIds,
      brandId: criteria.brandId,
      teamId: criteria.teamId,
      cleaningStatus: criteria.exteriorCleaningStatus ? [criteria.exteriorCleaningStatus] : [],
      interiorCleaningStatus: criteria.interiorCleaningStatus ? [criteria.interiorCleaningStatus] : [],
    });

    return parseCleaningTasks(response.data.cleaningWithoutPriority);
  }

  static async loadInvoicingSummary(criteria: TaskCriteria): Promise<InvoicingSummary> {
    const params = {
      moboOutletIds: criteria.outletIds,
      brandId: criteria.brandId,
      teamId: criteria.teamId,
      isResolved: criteria.status === TaskStatus.RESOLVED,
    };

    const response = await axios.post('/api/mobopro/v1/dossiers/tobeinvoiced', params);
    return present(parseInvoicingSummary(response.data));
  }

  static async loadAssignedDossierTasks(criteria: AssignedDossiersCriteria): Promise<Array<AssignedDossierTask>> {
    const data = {
      moboOutletIds: criteria.outletIds,
      brandId: criteria.brandId,
      teamId: criteria.teamId,
    };

    const response = await axios.post('/api/mobopro/v1/dossiers/assigned', data);
    return present(parseAssignedDossierTasks(response.data));
  }

  static async markAsResolved(taskId: TaskId): Promise<void> {
    await axios.put(`/api/mobopro/v1/eventtasks/${taskId}/status`, { resolved: true });
  }

  static async markAsUnresolved(taskId: TaskId): Promise<void> {
    await axios.put(`/api/mobopro/v1/eventtasks/${taskId}/status`, { resolved: false });
  }

  static async takeOwnership(taskId: TaskId): Promise<void> {
    await axios.put(`/api/mobopro/v1/eventtasks/${taskId}/ownership`, { owner: true });
  }

  static async releaseOwnership(taskId: TaskId): Promise<void> {
    await axios.put(`/api/mobopro/v1/eventtasks/${taskId}/ownership`, { owner: false });
  }

  static async assignGroup(taskId: TaskId, groupId: ChatGroupId): Promise<void> {
    await axios.put(`/api/mobopro/v1/eventtasks/${taskId}/assignment`, { assignToGroup: groupId });
  }

  static async assignUser(taskId: TaskId, userId: UserId): Promise<void> {
    await axios.put(`/api/mobopro/v1/eventtasks/${taskId}/assignment`, { assignToOutletUser: userId });
  }

  static async handleKeyDrop(taskId: TaskId, email: string | null, mobileNumber: string | null): Promise<void> {
    await axios.put(`/api/mobopro/v1/eventtasks/${taskId}/keydrop`, {
      targetEmail: email,
      targetMobileNumber: mobileNumber,
    });
  }

  static async updatePresence(isPresent: boolean, pin: string, outletId: OutletId): Promise<void> {
    await axios.post('/api/mobopro/v1/presences', {
      isClockedIn: isPresent,
      moboOutletId: outletId,
      pin,
    });
  }
}
